import { Component, OnInit, ViewChild } from '@angular/core';
import {
  PoBreadcrumb,
  PoModalComponent,
  PoNotificationService,
  PoTableAction,
  PoTableColumn,
  PoTableColumnLabel
} from '@po-ui/ng-components';
import { PoPageDynamicTableCustomAction } from '@po-ui/ng-templates';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { RpaService } from '../../core/rpa/rpa.service';
import {
  RpaProcessamento,
  RpaProcessamentoEmpresaStatus,
  RpaProcessamentoService
} from '../../core/rpa/rpa-processamento.service';
import { EmpresaRpaDto } from '../../models/Empresas/empresa-rpa-dto';
import { catchError, forkJoin, map, of } from 'rxjs';
import {
  COMPETENCIA_RELATORIO_OPTIONS,
  TIPO_PENDENCIA_RELATORIO_OPTIONS,
  TYPE_CND_PENDENCIA,
  TipoPendenciaRelatorio
} from './rpa-relatorio-pendencias.config';
import {
  RELATORIO_TAG_FILTER_OPTIONS,
  RELATORIO_TAG_FILTER_OPTIONS_INSCRITA,
  TagFiltroChip,
  estiloChipNgStyle,
  montarFiltrosTagsEstaticos,
  resolverTagsParaFiltroApi
} from './analise-pendencia/analise-pendencia-tags.config';

export type RpaTableRowType = 'processamento' | 'arquivo';

export interface RpaTableRow {
  rowType: RpaTableRowType;
  processamentoId?: string;
  key: string;
  competencia: string;
  fileName: string;
  dataHoraProc: string;
  status: string;
  fileUrl?: string;
  raw?: any;
}

@Component({
  selector: 'app-rpas',
  templateUrl: './rpas.component.html',
  styleUrls: ['./rpas.component.css'],
  standalone: false
})
export class RPAsComponent implements OnInit {
  @ViewChild('modalProcessamento') modalProcessamento!: PoModalComponent;
  @ViewChild('modalRelatorioPendencias') modalRelatorioPendencias!: PoModalComponent;

  tableItems: RpaTableRow[] = [];
  loadingTable = false;
  loadingRelatorioPendencias = false;
  reprocessandoId = '';
  processamentoSelecionado: RpaProcessamento | null = null;

  competenciaRelatorio = '';
  tipoPendenciaRelatorio: TipoPendenciaRelatorio = 'estadual-nao-inscrita';
  tagsRelatorioFiltro: TagFiltroChip[] = [];
  filtrosRelatorioTagsAtivos: Record<string, boolean> = {};

  readonly competenciaRelatorioOptions = COMPETENCIA_RELATORIO_OPTIONS;
  readonly tipoPendenciaRelatorioOptions = TIPO_PENDENCIA_RELATORIO_OPTIONS;

  readonly statusLabels: PoTableColumnLabel[] = [
    { value: 'PROCESSANDO', color: 'color-11', label: 'Processando', textColor: 'white' },
    { value: 'CANCELADO', color: 'color-05', label: 'Cancelado', textColor: 'white' },
    { value: 'SUCCESS', color: 'color-10', label: 'Emitido', textColor: 'white' },
    { value: 'BLOQUED', color: 'color-07', label: 'Bloqueado', textColor: 'white' },
    { value: 'UNEXPECTED', color: 'color-08', label: 'Erro', textColor: 'white' },
    { value: 'INVALID_CNPJ', color: 'color-05', label: 'CNPJ Inválido', textColor: 'white' }
  ];

  readonly columns: PoTableColumn[] = [
    { property: 'key', label: 'Chave' },
    { property: 'competencia', label: 'Competência' },
    { property: 'fileName', label: 'Nome do Arquivo' },
    {
      property: 'dataHoraProc',
      label: 'Processado em',
      type: 'date',
      format: 'dd/MM/yyyy HH:mm'
    },
    {
      property: 'status',
      label: 'Status',
      type: 'label',
      labels: this.statusLabels
    }
  ];

  readonly tableActions: PoTableAction[] = [
    {
      label: 'Visualizar processamento',
      action: this.abrirProcessamento.bind(this),
      icon: 'an an-list-bullets',
      visible: (row: RpaTableRow) => row.rowType === 'processamento'
    },
    {
      label: 'Cancelar processamento',
      action: this.cancelarProcessamento.bind(this),
      icon: 'an an-x-circle',
      type: 'danger',
      visible: (row: RpaTableRow) =>
        row.rowType === 'processamento' && row.status === 'PROCESSANDO'
    },
    {
      label: 'Reprocessar',
      action: this.reprocessarProcessamento.bind(this),
      icon: 'an an-arrows-clockwise',
      visible: (row: RpaTableRow) =>
        row.rowType === 'processamento' && row.status !== 'PROCESSANDO'
    },
    {
      label: 'Visualizar arquivo',
      action: this.visualizarArquivo.bind(this),
      icon: 'an an-eye',
      visible: (row: RpaTableRow) => row.rowType === 'arquivo' && this.podeVisualizarArquivo(row)
    }
  ];

  pageCustomActions: PoPageDynamicTableCustomAction[] = [
    {
      label: 'Relatório',
      action: () => this.router.navigate(['rpa/relatorio']),
      icon: 'an an-file-arrow-down'
    },
    {
      label: 'Relatório Pendências',
      action: () => this.router.navigate(['relatorio-pendencias/consulta']),
      icon: 'an an-file-arrow-down'
    }
  ];

  readonly breadcrumb: PoBreadcrumb = {
    items: [{ label: 'Home', link: '/home' }, { label: 'RPA' }, { label: 'RPA Disponíveis' }]
  };

  constructor(
    private readonly router: Router,
    private readonly rpaService: RpaService,
    private readonly rpaProcessamentoService: RpaProcessamentoService,
    private readonly poNotificationService: PoNotificationService
  ) {}

  ngOnInit(): void {
    this.carregarTabela();
  }

  atualizarTabela(): void {
    this.carregarTabela();
  }

  abrirProcessamento(row: RpaTableRow): void {
    if (!row.processamentoId) return;
    this.processamentoSelecionado =
      this.rpaProcessamentoService.getById(row.processamentoId) ?? null;
    if (!this.processamentoSelecionado) {
      this.poNotificationService.warning('Processamento não encontrado.');
      return;
    }
    this.modalProcessamento.open();
  }

  fecharModalProcessamento(): void {
    this.modalProcessamento.close();
    this.processamentoSelecionado = null;
  }

  cancelarProcessamento(row: RpaTableRow): void {
    if (!row.processamentoId) return;

    const cancelado = this.rpaProcessamentoService.cancelarProcessamento(row.processamentoId);
    if (!cancelado) {
      this.poNotificationService.warning('Somente processamentos em andamento podem ser cancelados.');
      return;
    }

    if (this.processamentoSelecionado?.id === row.processamentoId) {
      this.processamentoSelecionado =
        this.rpaProcessamentoService.getById(row.processamentoId) ?? null;
    }

    this.atualizarTabela();
    this.poNotificationService.success('Processamento cancelado.');
  }

  reprocessarProcessamento(row: RpaTableRow): void {
    if (!row.processamentoId || this.reprocessandoId) return;

    const proc = this.rpaProcessamentoService.getById(row.processamentoId);
    if (!proc) {
      this.poNotificationService.warning('Processamento não encontrado.');
      return;
    }

    const codigos = proc.codigosRastreio
      .map((codigo) => String(codigo ?? '').trim())
      .filter((codigo) => !!codigo && codigo !== 'GERAL');

    if (codigos.length === 0) {
      this.poNotificationService.warning(
        'Não há códigos de empresa salvos para reprocessar. Envie novamente pela tela de Relatório Pendências.'
      );
      return;
    }

    this.reprocessandoId = row.processamentoId;
    const tipoApi = proc.tipoDivida === 'nao-inscrita' ? 'naoInscrita' : 'inscrita';
    const pareceCnpj = codigos.some((codigo) => String(codigo).replace(/\D/g, '').length >= 11);
    const request$ = pareceCnpj
      ? this.rpaService.requestPendenciaCnpjsPorLista(tipoApi, codigos)
      : proc.tipoDivida === 'nao-inscrita'
        ? this.rpaService.requestPendenciaEstadualNaoInscritaPorCodigos(codigos)
        : this.rpaService.requestPendenciaEstadualPorCodigos(codigos);

    request$.subscribe({
      next: (response) => {
        const atualizado = this.rpaProcessamentoService.reiniciarProcessamento(
          row.processamentoId!,
          response
        );
        this.reprocessandoId = '';

        if (!atualizado) {
          this.poNotificationService.error('Não foi possível reiniciar o processamento.');
          return;
        }

        if (this.processamentoSelecionado?.id === row.processamentoId) {
          this.processamentoSelecionado = atualizado;
        }

        this.atualizarTabela();
        this.poNotificationService.success('Reprocessamento solicitado com sucesso.');
      },
      error: (err: { error?: { error?: string; message?: string } }) => {
        this.reprocessandoId = '';
        const mensagem = err?.error?.error || err?.error?.message || 'Erro ao solicitar reprocessamento.';
        this.poNotificationService.error(mensagem);
      }
    });
  }

  get podeCancelarProcessamentoModal(): boolean {
    return this.processamentoSelecionado?.status === 'PROCESSANDO';
  }

  get podeReprocessarProcessamentoModal(): boolean {
    return !!this.processamentoSelecionado && this.processamentoSelecionado.status !== 'PROCESSANDO';
  }

  cancelarProcessamentoModal(): void {
    if (!this.processamentoSelecionado) return;
    this.cancelarProcessamento({
      rowType: 'processamento',
      processamentoId: this.processamentoSelecionado.id,
      key: '',
      competencia: '',
      fileName: '',
      dataHoraProc: '',
      status: 'PROCESSANDO'
    });
  }

  reprocessarProcessamentoModal(): void {
    if (!this.processamentoSelecionado) return;
    this.reprocessarProcessamento({
      rowType: 'processamento',
      processamentoId: this.processamentoSelecionado.id,
      key: '',
      competencia: '',
      fileName: '',
      dataHoraProc: '',
      status: this.processamentoSelecionado.status === 'CANCELADO' ? 'CANCELADO' : 'BLOQUED'
    });
  }

  podeVisualizarArquivo(row: RpaTableRow): boolean {
    return !!this.rpaService.resolverObjectNameArquivo(row.raw ?? row);
  }

  visualizarArquivo(row: RpaTableRow): void {
    const objectName = this.rpaService.resolverObjectNameArquivo(row.raw ?? row);
    if (!objectName) {
      this.poNotificationService.warning('Arquivo ainda não disponível.');
      return;
    }
    this.rpaService.requestRpaUrlFile(objectName).subscribe({
      next: (response: any) => {
        const url = this.rpaService.extrairUrlAssinadaResposta(response);
        if (!url) {
          this.poNotificationService.error('A API não retornou a URL do arquivo.');
          return;
        }
        window.open(url, '_blank');
      },
      error: () => this.poNotificationService.error('Erro ao carregar o arquivo.')
    });
  }

  get empresasModalItems(): Array<{
    codigo: string;
    statusLabel: string;
    mensagem: string;
    atualizadoEm: string;
  }> {
    return (
      this.processamentoSelecionado?.empresas.map((emp) => ({
        codigo: emp.codigo,
        statusLabel: this.getStatusEmpresaLabel(emp.status),
        mensagem: emp.mensagem,
        atualizadoEm: emp.atualizadoEm
      })) ?? []
    );
  }

  irRelatorio(): void {
    this.router.navigate(['rpa/relatorio']);
  }

  irAnalisePendencia(): void {
    this.router.navigate(['relatorio-pendencias']);
  }

  onChangeCompetenciaRelatorio(value: string): void {
    this.competenciaRelatorio = value;
  }

  onChangeTipoPendenciaRelatorio(value: TipoPendenciaRelatorio): void {
    this.tipoPendenciaRelatorio = value;
    if (value === 'estadual-nao-inscrita' || value === 'estadual-inscrita') {
      this.inicializarFiltrosTagsRelatorio();
    } else {
      this.tagsRelatorioFiltro = [];
      this.filtrosRelatorioTagsAtivos = {};
    }
  }

  get exibirFiltroTagsRelatorio(): boolean {
    return (
      this.tipoPendenciaRelatorio === 'estadual-nao-inscrita' ||
      this.tipoPendenciaRelatorio === 'estadual-inscrita'
    );
  }

  get possuiFiltrosTagsRelatorio(): boolean {
    return this.tagsRelatorioFiltro.length > 0;
  }

  abrirRelatorioPendencias(): void {
    this.competenciaRelatorio = '';
    this.tipoPendenciaRelatorio = 'estadual-nao-inscrita';
    this.inicializarFiltrosTagsRelatorio();
    this.modalRelatorioPendencias.open();
  }

  alternarFiltroTagRelatorio(tag: string): void {
    this.filtrosRelatorioTagsAtivos = {
      ...this.filtrosRelatorioTagsAtivos,
      [tag]: !this.filtrosRelatorioTagsAtivos[tag]
    };
  }

  filtroTagRelatorioAtivo(tag: string): boolean {
    return !!this.filtrosRelatorioTagsAtivos[tag];
  }

  estiloChipRelatorio(tagFiltro: TagFiltroChip): Record<string, string> {
    return estiloChipNgStyle(tagFiltro.estilo);
  }

  private inicializarFiltrosTagsRelatorio(): void {
    const opcoes =
      this.tipoPendenciaRelatorio === 'estadual-inscrita'
        ? RELATORIO_TAG_FILTER_OPTIONS_INSCRITA
        : RELATORIO_TAG_FILTER_OPTIONS;

    const { chips, filtrosAtivos } = montarFiltrosTagsEstaticos(
      opcoes,
      this.filtrosRelatorioTagsAtivos
    );
    this.tagsRelatorioFiltro = chips;
    this.filtrosRelatorioTagsAtivos = filtrosAtivos;
  }

  fecharRelatorioPendencias(): void {
    this.modalRelatorioPendencias.close();
  }

  gerarRelatorioPendencias(): void {
    if (!this.competenciaRelatorio) {
      this.poNotificationService.warning('Selecione a competência (mês/ano).');
      return;
    }

    if (
      this.exibirFiltroTagsRelatorio &&
      this.tagsRelatorioFiltro.length > 0 &&
      !this.tagsRelatorioFiltro.some((chip) => this.filtrosRelatorioTagsAtivos[chip.tag])
    ) {
      this.poNotificationService.warning('Selecione ao menos uma tag para filtrar.');
      return;
    }

    const typeCnd = TYPE_CND_PENDENCIA[this.tipoPendenciaRelatorio];
    const tagsApi = this.exibirFiltroTagsRelatorio
      ? resolverTagsParaFiltroApi(this.tagsRelatorioFiltro, this.filtrosRelatorioTagsAtivos)
      : undefined;

    this.loadingRelatorioPendencias = true;

    const request$ =
      this.tipoPendenciaRelatorio === 'estadual-nao-inscrita'
        ? this.rpaService.requestRelatorioPendenciasNaoInscrita(this.competenciaRelatorio, tagsApi)
        : this.rpaService.requestRpaSearchPendencias(
            typeCnd,
            this.competenciaRelatorio,
            tagsApi && tagsApi.length > 0 ? 'Todos' : 'ComEmisssao',
            tagsApi
          );

    request$.subscribe({
      next: (response) => {
        const data = this.rpaService.normalizarListaEmpresas(response);

        if (data.length === 0) {
          this.loadingRelatorioPendencias = false;
          this.poNotificationService.warning('Nenhum registro encontrado com os filtros selecionados.');
          return;
        }

        const nomeArquivo = `pendencias_${this.tipoPendenciaRelatorio}`;
        this.exportRelatorioCsv(data, nomeArquivo);
        this.downloadRelatorioZip(data);
        this.loadingRelatorioPendencias = false;
        this.fecharRelatorioPendencias();
      },
      error: () => {
        this.loadingRelatorioPendencias = false;
        this.poNotificationService.error('Erro ao gerar relatório de pendências.');
      }
    });
  }

  private exportRelatorioCsv(data: EmpresaRpaDto[], fileName: string): void {
    if (data.length === 0) {
      this.poNotificationService.warning('Nenhum registro encontrado para exportação.');
      return;
    }

    const rows = data.map((row) => ({
      codigoempresa: row.codigoEmpresa ?? '',
      nome: row.nome ?? '',
      cnpj: row.cnpj ?? '',
      cidade: row.cidade ?? '',
      rpaCompetencia: row.rpaCompetencia ?? '',
      rpaStatus: row.rpaStatus ?? '',
      rpaKey: row.rpaKey ?? '',
      rpaTags: (row.rpaTags ?? []).join(' | '),
      rpaUrl: row.rpaUrl ?? ''
    }));

    const header = Object.keys(rows[0]).join(';') + '\n';
    const body = rows.map((row) => Object.values(row).join(';')).join('\n');
    const blob = new Blob([header + body], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}_${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private downloadRelatorioZip(data: EmpresaRpaDto[]): void {
    const objectNames = data
      .map((item) => this.rpaService.resolverObjectNameArquivo(item))
      .filter((name) => !!name);

    if (objectNames.length === 0) {
      this.poNotificationService.warning(
        'CSV gerado. Nenhum arquivo encontrado para montar o ZIP.'
      );
      return;
    }

    const batches = this.chunkArray(objectNames, 100);
    this.processarLotesZip(batches);
  }

  private chunkArray<T>(array: T[], size: number): T[][] {
    return Array.from({ length: Math.ceil(array.length / size) }, (_, i) =>
      array.slice(i * size, i * size + size)
    );
  }

  private processarLotesZip(batches: string[][]): void {
    let indice = 0;

    const processarProximo = (): void => {
      if (indice >= batches.length) {
        this.poNotificationService.success('Relatório gerado: CSV e ZIP baixados com sucesso.');
        return;
      }

      const lote = indice;
      this.rpaService.baixarZipLoteStream(batches[lote]).subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `pendencias_${new Date().toISOString()}-lote${lote + 1}.zip`;
          a.click();
          window.URL.revokeObjectURL(url);
          indice++;
          processarProximo();
        },
        error: () => {
          this.poNotificationService.error(`Erro ao baixar o lote ${lote + 1} do ZIP.`);
        }
      });
    };

    processarProximo();
  }

  getStatusEmpresaLabel(status: RpaProcessamentoEmpresaStatus): string {
    if (status === 'PRONTO') return 'Pronto';
    if (status === 'BLOQUEADO') return 'Bloqueado';
    return 'Processando';
  }

  private carregarTabela(): void {
    this.loadingTable = true;

    const pendenciasComOutbox = this.rpaProcessamentoService
      .getProcessamentosParaTabela()
      .filter((p) => {
        const botId = String(p.botId ?? '').trim();
        return !!botId && p.status === 'PROCESSANDO';
      });

    const outbox$ =
      pendenciasComOutbox.length === 0
        ? of([] as Array<{ id: string; response: unknown }>)
        : forkJoin(
            pendenciasComOutbox.map((p) =>
              this.rpaService
                .requestPendenciaOutboxStatus(String(p.botId), p.inicio)
                .pipe(
                  map((response) => ({ id: p.id, response: response as unknown })),
                  catchError(() => of({ id: p.id, response: null as unknown }))
                )
            )
          );

    outbox$.subscribe({
      next: (resultados) => {
        for (const item of resultados) {
          if (item.response) {
            this.rpaProcessamentoService.atualizarComOutboxStatus(item.id, item.response);
          }
        }

        this.rpaService.requestRpaFiles().subscribe({
          next: (response: any) => {
            this.rpaProcessamentoService.atualizarComArquivos(response);
            if (this.processamentoSelecionado?.id) {
              this.processamentoSelecionado =
                this.rpaProcessamentoService.getById(this.processamentoSelecionado.id) ?? null;
            }
            this.aplicarRespostaNaTabela(response);
            this.loadingTable = false;
          },
          error: () => {
            this.loadingTable = false;
            this.aplicarTabelaSomenteLocal();
            this.poNotificationService.error('Erro ao carregar arquivos RPA.');
          }
        });
      },
      error: () => {
        this.loadingTable = false;
        this.aplicarTabelaSomenteLocal();
        this.poNotificationService.error(
          'Não foi possível atualizar o status. Tente novamente em instantes.'
        );
      }
    });
  }

  private aplicarTabelaSomenteLocal(): void {
    this.aplicarRespostaNaTabela(null);
  }

  private aplicarRespostaNaTabela(response: any): void {
    const arquivos = this.extrairArquivos(response);
    const procRows = this.rpaProcessamentoService
      .getProcessamentosParaTabela(arquivos)
      .map((p) => this.toProcessamentoRow(p));
    const fileRows = arquivos.map((f: any) => this.toArquivoRow(f));
    this.tableItems = [...procRows, ...fileRows];
  }

  private extrairArquivos(response: any): any[] {
    if (!response) return [];
    return response?.items || response?.data || (Array.isArray(response) ? response : []);
  }

  private toProcessamentoRow(proc: RpaProcessamento): RpaTableRow {
    const key = this.rpaProcessamentoService.chaveExibicaoPendencia(proc.tipoDivida);
    const statusTabela =
      proc.status === 'PROCESSANDO'
        ? 'PROCESSANDO'
        : proc.status === 'CANCELADO'
          ? 'CANCELADO'
          : proc.status === 'PRONTO'
            ? 'SUCCESS'
            : 'BLOQUED';

    return {
      rowType: 'processamento',
      processamentoId: proc.id,
      key,
      competencia: '—',
      fileName: `${proc.titulo} (${proc.totalEmpresas} empresa(s))`,
      dataHoraProc: proc.inicio,
      status: statusTabela
    };
  }

  private toArquivoRow(file: any): RpaTableRow {
    return {
      rowType: 'arquivo',
      key: String(file?.key ?? ''),
      competencia: String(file?.competencia ?? '—'),
      fileName: String(file?.fileName ?? ''),
      dataHoraProc: file?.dataHoraProc ?? file?.dataHoraProcessamento ?? '',
      status: String(file?.status ?? '').toUpperCase(),
      fileUrl: this.rpaService.resolverObjectNameArquivo(file) || undefined,
      raw: file
    };
  }
}
